import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronDown,
  Eye,
  FolderInput,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  MoreHorizontal,
  Pencil,
  Play,
  RefreshCw,
  Star,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import { MediaItem, uploadMedia } from '../lib/mediaLibrary';
import MediaLibraryPicker from './MediaLibraryPicker';

export interface GbpMediaItem {
  name: string;
  mediaFormat: 'PHOTO' | 'VIDEO' | string;
  category: string;
  googleUrl: string | null;
  thumbnailUrl: string | null;
  sourceUrl: string | null;
  description: string | null;
  createTime: string | null;
  width: number | null;
  height: number | null;
  viewCount: number | null;
  attribution: { profileName: string | null; profileUrl: string | null; profilePhotoUrl: string | null } | null;
}

/** Gallery categories a business can file photos under (cover/logo are roles, set separately). */
const GALLERY_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'ADDITIONAL', label: 'Additional' },
  { key: 'AT_WORK', label: 'At work' },
  { key: 'EXTERIOR', label: 'Exterior' },
  { key: 'INTERIOR', label: 'Interior' },
  { key: 'PRODUCT', label: 'Products' },
  { key: 'TEAMS', label: 'Team' },
  { key: 'COMMON_AREA', label: 'Common areas' },
  { key: 'ROOMS', label: 'Rooms' },
  { key: 'MENU', label: 'Menu' },
  { key: 'FOOD_AND_DRINK', label: 'Food & drink' },
];

const CATEGORY_LABELS: Record<string, string> = {
  COVER: 'Cover',
  PROFILE: 'Logo',
  LOGO: 'Logo',
  ...Object.fromEntries(GALLERY_CATEGORIES.map((c) => [c.key, c.label])),
};

function categoryLabel(key: string): string {
  return CATEGORY_LABELS[key] || key.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function isCover(item: GbpMediaItem) {
  return item.category === 'COVER';
}
function isLogo(item: GbpMediaItem) {
  return item.category === 'PROFILE' || item.category === 'LOGO';
}

function srcCandidates(item: GbpMediaItem): string[] {
  const out: string[] = [];
  for (const u of [item.googleUrl, item.thumbnailUrl, item.sourceUrl]) {
    const s = String(u || '').trim();
    if (s && /^https?:\/\//i.test(s) && !out.includes(s)) out.push(s);
  }
  return out;
}

function fmtViews(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  // Google returns the epoch for items whose upload date it no longer tracks.
  if (Number.isNaN(d.getTime()) || d.getTime() <= 0) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Google user-content URLs 403 with a Referer — strip it and fall back across the CDN variants. */
/** Ask Google's CDN for a thumbnail rather than the multi-megapixel original. */
function gbpThumb(url: string, size: number, opts?: { crop?: boolean }): string {
  if (!/^https:\/\/lh\d\.googleusercontent\.com\//i.test(url)) return url;
  return `${url.replace(/=[^/]*$/, '')}=${opts?.crop === false ? `s${size}` : `w${size}-h${size}-c`}`;
}

function MediaImg({
  item,
  className,
  alt,
  size,
  crop = true,
}: {
  item: GbpMediaItem;
  className?: string;
  alt?: string;
  /** Thumbnail size in px; omit for the full-resolution original. */
  size?: number;
  crop?: boolean;
}) {
  const candidates = srcCandidates(item).map((u) => (size ? gbpThumb(u, size, { crop }) : u));
  const [idx, setIdx] = useState(0);
  const src = candidates[idx] || '';
  if (!src) {
    return (
      <div className={`flex items-center justify-center text-gray-300 bg-gray-50 ${className || ''}`}>
        <ImageIcon className="h-7 w-7" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt ?? item.description ?? ''}
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        if (idx + 1 < candidates.length) setIdx(idx + 1);
      }}
    />
  );
}

type PendingUpload = { urls: string[]; role: 'COVER' | 'PROFILE' | null };

interface GoogleBusinessPhotosPanelProps {
  businessId: string;
  connectionId: string;
  open: boolean;
  onClose: () => void;
  /** Called after any change so the parent can refresh its avatar/cover. */
  onChanged?: (items: GbpMediaItem[]) => void;
  /** Open with the cover/logo "Change" menu already expanded. */
  initialRoleMenu?: 'COVER' | 'PROFILE' | null;
  /** Open straight onto a photo's detail view (Google media name). */
  initialDetailName?: string | null;
}

export default function GoogleBusinessPhotosPanel({
  businessId,
  connectionId,
  open,
  onClose,
  onChanged,
  initialRoleMenu = null,
  initialDetailName = null,
}: GoogleBusinessPhotosPanelProps) {
  const [items, setItems] = useState<GbpMediaItem[]>([]);
  const [customerItems, setCustomerItems] = useState<GbpMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState<string>('ALL');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [moveFor, setMoveFor] = useState<string | null>(null);
  const [detail, setDetail] = useState<GbpMediaItem | null>(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [showCustomers, setShowCustomers] = useState(false);
  const [roleMenu, setRoleMenu] = useState<'COVER' | 'PROFILE' | null>(null);
  const [rolePickFromGallery, setRolePickFromGallery] = useState<'COVER' | 'PROFILE' | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [pendingCategory, setPendingCategory] = useState('ADDITIONAL');
  const [pendingCaption, setPendingCaption] = useState('');
  /** Which role (if any) the next chosen file/library pick is for. */
  const [pickRole, setPickRole] = useState<'COVER' | 'PROFILE' | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const invoke = useCallback(
    async (body: Record<string, unknown>) => {
      const { data, error } = await supabase.functions.invoke('google-gbp-media', {
        body: { businessId, connectionId, ...body },
      });
      if (error || data?.error) {
        throw new Error((await invokeErrorMessage(error, data)) || 'Request failed');
      }
      return data;
    },
    [businessId, connectionId],
  );

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      setLoading(true);
      try {
        const data = await invoke({ action: 'list' });
        const list = (Array.isArray(data?.mediaItems) ? data.mediaItems : []) as GbpMediaItem[];
        const customers = (Array.isArray(data?.customerMediaItems) ? data.customerMediaItems : []) as GbpMediaItem[];
        setItems(list);
        setCustomerItems(customers);
        // With an empty owner gallery, customer photos are the only thing to
        // see — open that section so the listing doesn't look bare.
        if (list.filter((i) => !isCover(i) && !isLogo(i)).length === 0 && customers.length > 0) {
          setShowCustomers(true);
        }
        setLoaded(true);
        onChanged?.(list);
      } catch (err) {
        if (!opts?.silent) toast.error(err instanceof Error ? err.message : 'Failed to load photos');
      } finally {
        setLoading(false);
      }
    },
    [invoke, onChanged],
  );

  useEffect(() => {
    if (open && !loaded) void load();
  }, [open, loaded, load]);

  // Deep-link entry points from the workspace (cover "Change", thumbnail tap).
  useEffect(() => {
    if (!open) return;
    if (initialRoleMenu) setRoleMenu(initialRoleMenu);
  }, [open, initialRoleMenu]);

  useEffect(() => {
    if (!open || !initialDetailName || !loaded) return;
    const item = items.find((i) => i.name === initialDetailName);
    if (item) {
      setDetail(item);
      setCaptionDraft(item.description || '');
    }
  }, [open, initialDetailName, loaded, items]);

  useEffect(() => {
    // Reload on reopen if the connection changed underneath us.
    setLoaded(false);
    setItems([]);
    setCustomerItems([]);
  }, [connectionId]);

  const cover = useMemo(() => items.find(isCover) || null, [items]);
  const logo = useMemo(() => items.find(isLogo) || null, [items]);
  const gallery = useMemo(() => items.filter((i) => !isCover(i) && !isLogo(i)), [items]);
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of gallery) counts.set(g.category, (counts.get(g.category) || 0) + 1);
    return counts;
  }, [gallery]);
  const visible = useMemo(
    () => (filter === 'ALL' ? gallery : gallery.filter((g) => g.category === filter)),
    [gallery, filter],
  );
  const totalViews = useMemo(
    () => items.reduce((sum, i) => sum + (i.viewCount || 0), 0),
    [items],
  );

  const run = async (name: string | null, label: string, fn: () => Promise<void>) => {
    setBusyName(name);
    try {
      await fn();
      toast.success(label);
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${label.toLowerCase()}`);
    } finally {
      setBusyName(null);
      setMenuFor(null);
      setMoveFor(null);
    }
  };

  const setRole = (item: GbpMediaItem, role: 'COVER' | 'PROFILE') =>
    run(item.name, role === 'COVER' ? 'Cover photo updated' : 'Logo updated', async () => {
      await invoke({
        action: 'setRole',
        name: item.name,
        role,
        googleUrl: item.googleUrl,
        sourceUrl: item.sourceUrl,
        description: item.description,
        width: item.width,
        height: item.height,
      });
      setRolePickFromGallery(null);
      setDetail(null);
    });

  const moveTo = (item: GbpMediaItem, category: string) =>
    run(item.name, `Moved to ${categoryLabel(category)}`, async () => {
      await invoke({ action: 'update', name: item.name, category });
      setDetail((d) => (d && d.name === item.name ? { ...d, category } : d));
    });

  const saveCaption = (item: GbpMediaItem, caption: string) =>
    run(item.name, 'Caption saved', async () => {
      await invoke({ action: 'update', name: item.name, description: caption.trim() || null });
      setDetail((d) => (d && d.name === item.name ? { ...d, description: caption.trim() || null } : d));
    });

  const remove = (item: GbpMediaItem) => {
    const what = isCover(item) ? 'the cover photo' : isLogo(item) ? 'the logo' : 'this photo';
    if (!window.confirm(`Remove ${what} from your Google Business Profile? This can't be undone.`)) return;
    return run(item.name, 'Photo removed', async () => {
      await invoke({ action: 'delete', name: item.name });
      setDetail(null);
    });
  };

  // ── Adding media ──
  const beginPick = (source: 'file' | 'library', role: 'COVER' | 'PROFILE' | null) => {
    setPickRole(role);
    setRoleMenu(null);
    if (source === 'file') fileInputRef.current?.click();
    else setShowMediaPicker(true);
  };

  const queueUpload = (urls: string[], role: 'COVER' | 'PROFILE' | null) => {
    if (urls.length === 0) return;
    if (role) {
      // Roles skip the category dialog.
      void commitUpload({ urls, role }, role, '');
      return;
    }
    setPendingCategory(filter !== 'ALL' ? filter : 'ADDITIONAL');
    setPendingCaption('');
    setPendingUpload({ urls, role: null });
  };

  const commitUpload = async (pending: PendingUpload, category: string, caption: string) => {
    setUploading(true);
    setPendingUpload(null);
    try {
      let ok = 0;
      let lastError: string | null = null;
      for (const url of pending.urls) {
        try {
          const isVideo = /\.(mp4|mov|m4v|webm)(\?|$)/i.test(url);
          await invoke({
            action: 'upload',
            sourceUrl: url,
            category,
            description: caption || null,
            mediaFormat: isVideo ? 'VIDEO' : 'PHOTO',
          });
          ok += 1;
        } catch (err) {
          lastError = err instanceof Error ? err.message : 'Upload failed';
        }
      }
      if (ok > 0) {
        toast.success(
          pending.role === 'COVER'
            ? 'Cover photo updated'
            : pending.role === 'PROFILE'
              ? 'Logo updated'
              : ok === 1
                ? 'Photo added to Google Business Profile'
                : `${ok} photos added to Google Business Profile`,
        );
      }
      if (lastError) toast.error(lastError);
      await load({ silent: true });
    } finally {
      setUploading(false);
      setPickRole(null);
    }
  };

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    const media = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (media.length === 0) {
      toast.error('Please choose image or video files');
      return;
    }
    const role = pickRole;
    setUploading(true);
    try {
      const created = await uploadMedia(businessId, role ? media.slice(0, 1) : media);
      const urls = created.map((c) => c.url).filter(Boolean) as string[];
      if (urls.length === 0) throw new Error('Upload did not return a URL');
      setUploading(false);
      queueUpload(urls, role);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
      setUploading(false);
      setPickRole(null);
    }
  };

  const handleLibraryPick = (selected: MediaItem[]) => {
    setShowMediaPicker(false);
    const role = pickRole;
    const urls = (role ? selected.slice(0, 1) : selected).map((m) => m.url).filter(Boolean) as string[];
    if (urls.length === 0) {
      setPickRole(null);
      return;
    }
    queueUpload(urls, role);
  };

  if (!open) return null;

  const roleCard = (
    role: 'COVER' | 'PROFILE',
    item: GbpMediaItem | null,
    aspect: string,
  ) => {
    const busy = (item && busyName === item.name) || (uploading && pickRole === role);
    return (
      // Clipping lives on the image box, not the card, so the Change menu can hang outside it.
      <div className={`relative rounded-xl border-2 ${role === 'COVER' ? 'border-[#1a73e8] ring-2 ring-blue-100' : 'border-gray-200'} bg-gray-50 ${roleMenu === role ? 'z-30' : ''}`}>
        <div className={`${aspect} rounded-[10px] overflow-hidden`}>
          {item ? (
            <button type="button" className="w-full h-full" onClick={() => { setDetail(item); setCaptionDraft(item.description || ''); }}>
              <MediaImg
                item={item}
                className="w-full h-full object-cover"
                size={role === 'COVER' ? 1600 : 400}
                crop={role !== 'COVER'}
              />
            </button>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-2">
              <ImageIcon className="h-6 w-6 text-gray-300 mb-1" />
              <p className="text-[11px] text-gray-500">No {role === 'COVER' ? 'cover' : 'logo'} yet</p>
            </div>
          )}
        </div>
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-900/80 text-white">
          <Star className="h-2.5 w-2.5" />
          {role === 'COVER' ? 'Cover' : 'Logo'}
        </span>
        {item?.viewCount != null ? (
          <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-black/55 text-white">
            <Eye className="h-2.5 w-2.5" />
            {fmtViews(item.viewCount)}
          </span>
        ) : null}
        <div className="absolute top-1.5 right-1.5">
          <button
            type="button"
            onClick={() => setRoleMenu((r) => (r === role ? null : role))}
            disabled={busy || uploading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 border border-gray-200 text-gray-800 text-[10px] font-semibold shadow-sm hover:bg-white disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pencil className="h-3 w-3" />}
            {item ? 'Change' : 'Add'}
          </button>
          {roleMenu === role ? (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setRoleMenu(null)} />
              <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1 overflow-hidden">
                <button type="button" onClick={() => beginPick('file', role)} className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50">
                  Upload new
                </button>
                <button type="button" onClick={() => beginPick('library', role)} className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50">
                  From Media library
                </button>
                <button
                  type="button"
                  onClick={() => { setRoleMenu(null); setRolePickFromGallery(role); }}
                  disabled={gallery.length === 0}
                  className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                >
                  Choose from gallery
                </button>
                {item ? (
                  <button
                    type="button"
                    onClick={() => { setRoleMenu(null); void remove(item); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                  >
                    Remove {role === 'COVER' ? 'cover' : 'logo'}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100080] bg-white">
        <div
          className="h-full w-full flex flex-col overflow-hidden"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Google Business photos"
        >
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Google Business photos</h3>
              <p className="text-[11px] text-gray-500 truncate">
                {items.length} {items.length === 1 ? 'item' : 'items'}
                {totalViews > 0 ? ` · ${fmtViews(totalViews)} total views` : ''}
                {customerItems.length > 0 ? ` · ${customerItems.length} from customers` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => void load()}
                disabled={loading}
                aria-label="Refresh"
                className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-5xl p-4 sm:p-6 space-y-6">
            {loading && !loaded ? (
              <div className="flex justify-center py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <>
                {/* Cover + logo */}
                <section>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    Profile look
                  </p>
                  <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                    <div className="max-w-2xl">{roleCard('COVER', cover, 'aspect-[16/9]')}</div>
                    <div className="w-[104px] sm:w-[128px]">{roleCard('PROFILE', logo, 'aspect-square')}</div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5">
                    Google chooses which photo customers see first, but the cover is its strongest hint.
                  </p>
                </section>

                {/* Gallery */}
                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Gallery
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => beginPick('library', null)}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <ImageIcon className="h-3.5 w-3.5" />
                        Media library
                      </button>
                      <button
                        type="button"
                        onClick={() => beginPick('file', null)}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-[#1a73e8] text-white hover:bg-[#1765cc] disabled:opacity-50"
                      >
                        {uploading && !pickRole ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <ImagePlus className="h-3.5 w-3.5" />
                        )}
                        Add photos
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/mp4,video/quicktime"
                        multiple
                        className="hidden"
                        onChange={(e) => void handleFiles(e)}
                      />
                    </div>
                  </div>

                  {gallery.length > 0 ? (
                    <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <button
                        type="button"
                        onClick={() => setFilter('ALL')}
                        className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                          filter === 'ALL' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'
                        }`}
                      >
                        All · {gallery.length}
                      </button>
                      {[...categoryCounts.entries()]
                        .sort((a, b) => b[1] - a[1])
                        .map(([cat, count]) => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setFilter(cat)}
                            className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                              filter === cat ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'
                            }`}
                          >
                            {categoryLabel(cat)} · {count}
                          </button>
                        ))}
                    </div>
                  ) : null}

                  {gallery.length === 0 ? (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl px-4 py-10 text-center">
                      <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 font-medium">No gallery photos yet</p>
                      <p className="text-[12px] text-gray-500 mt-0.5">
                        Profiles with 10+ photos get noticeably more calls and direction requests.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                      {visible.map((item) => {
                        const busy = busyName === item.name;
                        const open = menuFor === item.name;
                        return (
                          <div key={item.name} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-white">
                            <button
                              type="button"
                              className="block w-full aspect-square bg-gray-50"
                              onClick={() => { setDetail(item); setCaptionDraft(item.description || ''); }}
                            >
                              <MediaImg item={item} className="w-full h-full object-cover" size={400} />
                            </button>
                            {item.mediaFormat === 'VIDEO' ? (
                              <span className="absolute top-1.5 left-1.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                                <Play className="h-3 w-3" />
                              </span>
                            ) : null}
                            {item.viewCount != null ? (
                              <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-black/55 text-white">
                                <Eye className="h-2.5 w-2.5" />
                                {fmtViews(item.viewCount)}
                              </span>
                            ) : null}
                            <div className="absolute top-1.5 right-1.5">
                              <button
                                type="button"
                                onClick={() => { setMenuFor(open ? null : item.name); setMoveFor(null); }}
                                disabled={busy}
                                aria-label="Photo actions"
                                className="h-7 w-7 rounded-full bg-white/95 border border-gray-200 text-gray-700 shadow-sm flex items-center justify-center hover:bg-white disabled:opacity-50"
                              >
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
                              </button>
                              {open ? (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => { setMenuFor(null); setMoveFor(null); }} />
                                  <div className="absolute right-0 top-full mt-1 z-20 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1 overflow-hidden">
                                    {item.mediaFormat !== 'VIDEO' ? (
                                      <>
                                        <button type="button" onClick={() => void setRole(item, 'COVER')} className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 inline-flex items-center gap-2">
                                          <Star className="h-3.5 w-3.5 text-gray-400" /> Set as cover
                                        </button>
                                        <button type="button" onClick={() => void setRole(item, 'PROFILE')} className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 inline-flex items-center gap-2">
                                          <Star className="h-3.5 w-3.5 text-gray-400" /> Set as logo
                                        </button>
                                      </>
                                    ) : null}
                                    <button
                                      type="button"
                                      onClick={() => setMoveFor(moveFor === item.name ? null : item.name)}
                                      className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 inline-flex items-center justify-between gap-2"
                                    >
                                      <span className="inline-flex items-center gap-2">
                                        <FolderInput className="h-3.5 w-3.5 text-gray-400" /> Move to…
                                      </span>
                                      <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${moveFor === item.name ? 'rotate-180' : ''}`} />
                                    </button>
                                    {moveFor === item.name ? (
                                      <div className="max-h-44 overflow-y-auto border-y border-gray-100 bg-gray-50/60">
                                        {GALLERY_CATEGORIES.map((c) => (
                                          <button
                                            key={c.key}
                                            type="button"
                                            disabled={c.key === item.category}
                                            onClick={() => void moveTo(item, c.key)}
                                            className="w-full text-left pl-8 pr-3 py-1.5 text-[13px] text-gray-700 hover:bg-white disabled:opacity-50 inline-flex items-center justify-between"
                                          >
                                            {c.label}
                                            {c.key === item.category ? <Check className="h-3.5 w-3.5 text-[#1a73e8]" /> : null}
                                          </button>
                                        ))}
                                      </div>
                                    ) : null}
                                    <button
                                      type="button"
                                      onClick={() => { setMenuFor(null); setDetail(item); setCaptionDraft(item.description || ''); }}
                                      className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 inline-flex items-center gap-2"
                                    >
                                      <Pencil className="h-3.5 w-3.5 text-gray-400" /> Edit caption
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => void remove(item)}
                                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-2 border-t border-gray-100"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                  </div>
                                </>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Customer photos */}
                {customerItems.length > 0 ? (
                  <section>
                    <button
                      type="button"
                      onClick={() => setShowCustomers((v) => !v)}
                      className="w-full flex items-center justify-between gap-2 text-left"
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        From customers · {customerItems.length}
                      </p>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showCustomers ? 'rotate-180' : ''}`} />
                    </button>
                    {showCustomers ? (
                      <>
                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                          {customerItems.map((item) => (
                            <div key={item.name} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                              <div className="aspect-square">
                                <MediaImg item={item} className="w-full h-full object-cover" size={400} />
                              </div>
                              {item.attribution?.profileName ? (
                                <span className="absolute bottom-0 inset-x-0 px-1.5 py-1 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent truncate">
                                  {item.attribution.profileName}
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5">
                          Google files a photo by who uploaded it: anything posted through the Google
                          Maps app as a person — even by you or your staff — lands here, not in your
                          gallery. Only photos added as the business can be managed. Customer photos can
                          be reported to Google from the Business Profile app.
                        </p>
                      </>
                    ) : null}
                  </section>
                ) : null}
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Choose an existing gallery photo for cover/logo */}
      {rolePickFromGallery ? (
        <div className="fixed inset-0 z-[100090] bg-white">
          <div
            className="h-full w-full flex flex-col overflow-hidden"
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label={`Choose ${rolePickFromGallery === 'COVER' ? 'cover photo' : 'logo'}`}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Choose {rolePickFromGallery === 'COVER' ? 'cover photo' : 'logo'}
                </h3>
                <p className="text-[11px] text-gray-500">Tap a photo to use it</p>
              </div>
              <button type="button" onClick={() => setRolePickFromGallery(null)} className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {gallery.filter((g) => g.mediaFormat !== 'VIDEO').map((item) => (
                  <button
                    key={item.name}
                    type="button"
                    disabled={!!busyName}
                    onClick={() => void setRole(item, rolePickFromGallery)}
                    className="aspect-square rounded-xl overflow-hidden border border-gray-200 hover:border-[#1a73e8] hover:ring-2 hover:ring-blue-200 transition-all disabled:opacity-50 relative"
                  >
                    <MediaImg item={item} className="w-full h-full object-cover" size={400} />
                    {busyName === item.name ? (
                      <span className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#1a73e8]" /></span>
                    ) : null}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-3">
                Google re-imports the photo in its new role; the gallery copy is removed so nothing is duplicated.
              </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Category + caption for new uploads */}
      {pendingUpload ? (
        <div className="fixed inset-0 z-[100090] flex items-end sm:items-center justify-center p-0 sm:p-6">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPendingUpload(null)} />
          <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
              <h3 className="text-sm font-semibold text-gray-900">
                Add {pendingUpload.urls.length === 1 ? 'photo' : `${pendingUpload.urls.length} photos`}
              </h3>
              <button type="button" onClick={() => setPendingUpload(null)} className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
              <div className="flex gap-2 overflow-x-auto -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {pendingUpload.urls.slice(0, 6).map((u) => (
                  <img key={u} src={u} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0 bg-gray-100" />
                ))}
                {pendingUpload.urls.length > 6 ? (
                  <span className="h-16 w-16 rounded-lg bg-gray-100 text-gray-500 text-[12px] font-semibold flex items-center justify-center shrink-0">
                    +{pendingUpload.urls.length - 6}
                  </span>
                ) : null}
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Where should it go?</p>
                <div className="flex flex-wrap gap-1.5">
                  {GALLERY_CATEGORIES.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setPendingCategory(c.key)}
                      className={`px-2.5 py-1 rounded-full text-[12px] font-semibold border ${
                        pendingCategory === c.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 block" htmlFor="gbp-photo-caption">
                  Caption <span className="font-normal normal-case text-gray-400">(optional)</span>
                </label>
                <input
                  id="gbp-photo-caption"
                  value={pendingCaption}
                  onChange={(e) => setPendingCaption(e.target.value)}
                  maxLength={240}
                  placeholder="e.g. Ceramic coating on a Model 3"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <button
                type="button"
                onClick={() => void commitUpload(pendingUpload, pendingCategory, pendingCaption)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1a73e8] text-white text-sm font-semibold hover:bg-[#1765cc]"
              >
                <Upload className="h-4 w-4" />
                Add to Google Business Profile
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Detail / caption editor */}
      {detail ? (
        <div className="fixed inset-0 z-[100090] flex items-end sm:items-center justify-center p-0 sm:p-6">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail(null)} />
          <div className="relative bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
            <div className="bg-black flex items-center justify-center max-h-[55vh] shrink-0">
              <MediaImg item={detail} className="max-h-[55vh] w-auto max-w-full object-contain" size={1600} crop={false} />
            </div>
            <button type="button" onClick={() => setDetail(null)} className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
            <div className="p-4 space-y-3 flex-1 min-h-0 overflow-y-auto">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700 font-semibold">
                  {categoryLabel(detail.category)}
                </span>
                {detail.viewCount != null ? (
                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{detail.viewCount.toLocaleString()} views</span>
                ) : null}
                {detail.width && detail.height ? <span>{detail.width}×{detail.height}</span> : null}
                {detail.createTime ? <span>Added {fmtWhen(detail.createTime)}</span> : null}
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 block" htmlFor="gbp-detail-caption">Caption</label>
                <div className="flex gap-2">
                  <input
                    id="gbp-detail-caption"
                    value={captionDraft}
                    onChange={(e) => setCaptionDraft(e.target.value)}
                    maxLength={240}
                    placeholder="Describe this photo"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <button
                    type="button"
                    disabled={busyName === detail.name || captionDraft.trim() === (detail.description || '').trim()}
                    onClick={() => void saveCaption(detail, captionDraft)}
                    className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-40"
                  >
                    {busyName === detail.name ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {!isCover(detail) && detail.mediaFormat !== 'VIDEO' ? (
                  <button type="button" onClick={() => void setRole(detail, 'COVER')} disabled={!!busyName} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-[12px] font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                    <Star className="h-3.5 w-3.5" /> Set as cover
                  </button>
                ) : null}
                {!isLogo(detail) && detail.mediaFormat !== 'VIDEO' ? (
                  <button type="button" onClick={() => void setRole(detail, 'PROFILE')} disabled={!!busyName} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-[12px] font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                    <Star className="h-3.5 w-3.5" /> Set as logo
                  </button>
                ) : null}
                <button type="button" onClick={() => void remove(detail)} disabled={!!busyName} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-[12px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <MediaLibraryPicker
        isOpen={showMediaPicker}
        businessId={businessId}
        multiple={!pickRole}
        zIndex={100100}
        onClose={() => { setShowMediaPicker(false); setPickRole(null); }}
        onDone={handleLibraryPick}
      />
    </>,
    document.body,
  );
}
